import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from 'react-fetching-library';
import { styled } from '@mui/material/styles';
import { Box, Portal, Button, Backdrop, IconButton, Typography, FormControl, Select, MenuItem, InputLabel } from '@mui/material';

import { useResponsive } from '../../../services/hooks';
import Iconify from '../../../components-core/Iconify';
import { FullScreenView } from './order-details/FullScreenView';
import { SimpleView } from './order-details/SimpleView';
import { SnackbarAlert } from '../../../components-core/SnackbarAlert';
import ConfirmChangeStateModal from './ConfirmChangeStateModal';
import ConfirmCloseOrder from './ConfirmCloseOrder';

import { formatDate } from '../../../services/functions';
import { updateOrderAction } from '../../../services/request';
import { updateOrder, setOrder, setAllowAutoOrderUpdate } from '../../../services/redux-actions';

import useMediaQuery from '@mui/material/useMediaQuery';


const RootStyle = styled('div')(({ theme }) => ({
  right: 0,
  bottom: 0,
  zIndex: 1229,
  minHeight: 440,
  outline: 'none',
  display: 'flex',
  position: 'fixed',
  overflow: 'hidden',
  flexDirection: 'column',
  margin: theme.spacing(3),
  boxShadow: theme.customShadows.z20,
  borderRadius: Number(theme.shape.borderRadius) * 2,
  backgroundColor: theme.palette.background.paper
}));

OrderDetails.propTypes = {
  isOpenCompose: PropTypes.bool,
  onCloseCompose: PropTypes.func,
  updateOrders: PropTypes.func
};

export default function OrderDetails({ isOpenCompose, onCloseCompose, parentFullScreen, updateOrders, refreshOrders }) {
  const dispatch = useDispatch();
  const { order } = useSelector(state => state.app);
  const { mutate } = useMutation(updateOrderAction);
  const [fullScreen, setFullScreen] = React.useState(false);
  const [openSnackbar, setOpenSnackbar] = React.useState({ success: false, error: false });
  const [state, setState] = React.useState(order.state);
  const [openStateModal, setOpenStateModal] = React.useState(false);
  const [openConfirmCloseOrderModal, setOpenConfirmCloseOrderModal] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const refFormik = React.useRef();

  const isDesktop = useResponsive('up', 'sm');


  const handleOpenStateModal = () => setOpenStateModal(true);
  const handleCloseStateModal = () => {
    setState(order.state);
    setOpenStateModal(false);
  };

  const handleOpenConfirmCloseOrderModal = () => setOpenConfirmCloseOrderModal(true);
  const handleCloseConfirmCloseOrderModal = () => setOpenConfirmCloseOrderModal(false);

  const handleStateSubmit = async (orderId, values) => {
    const { error, payload } = await mutate(orderId, values);
    if (!error) {
      dispatch(updateOrder(payload));
      updateOrders(payload);
      setOpenStateModal(false);
      setOpenSnackbar(prev => ({ ...prev, success: true }));
      if (order.way_of_sending_contract === 'traditional_mail' && payload.state === 'CON') {
        setMessage('Status został zmieniony. Dokument powinny zostać wysłane pocztą tradycyjną');
      } else {
        setMessage('Status został zmieniony');
      }
    } else {
      setState(order.state);
      if (_.isArray(payload)) {
        setMessage(payload[0]);
      } else {
        setMessage('Nie udało się zmienić statusu');
      }
      setOpenStateModal(false);
      setOpenSnackbar(prev => ({ ...prev, error: true }));
    }
  };

  React.useEffect(() => {
    setState(order.state);
  }, [order]);

  React.useEffect(() => {
    setFullScreen(parentFullScreen);
  }, [parentFullScreen]);

  const handleChangeState = event => {
    setState(event.target.value);
    handleOpenStateModal();
  };

  const handleExitFullScreen = () => {
    setFullScreen(false);
  };

  const handleEnterFullScreen = () => {
    setFullScreen(true);
  };

  const handleClose = () => {
    onCloseCompose();
    setFullScreen(false);
    handleCloseConfirmCloseOrderModal();
    dispatch(setOrder({}));
    dispatch(setAllowAutoOrderUpdate(true));
    refreshOrders();
  };

  const handleConfirmClose = () => {
    if (fullScreen) {
      if (refFormik.current.dirty) {
        handleOpenConfirmCloseOrderModal();
      } else {
        handleClose();
      }
    } else {
      handleClose();
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpenSnackbar({ success: false, error: false });
  };

  const matches = useMediaQuery('(max-width:1024px)');


  React.useEffect(() => {
    if (isOpenCompose === true && matches) {
      setFullScreen(true);
    }
  }, [isOpenCompose]);


  if (!isOpenCompose) {
    return null;
  }

  return (
    <Portal>
      <Backdrop open={fullScreen || !isDesktop} sx={{ zIndex: 1228 }} />
      <RootStyle
        sx={{
          ...(fullScreen && {
            top: 0,
            left: 0,
            zIndex: 1229,
            margin: 'auto',
            width: {
              xs: 'calc(100% - 24px)',
              md: 'calc(100% - 20px)'
            },
            height: {
              xs: 'calc(100% - 24px)',
              md: 'calc(100% - 10px)'
            }
          })
        }}
      >
        <Box
          sx={{
            pl: 3,
            pr: 1,
            height: 60,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Typography variant="h6">{order.identifier ? order.identifier : ''}</Typography>
          <Box width={40} />
          <Typography variant="h6">{order.event_name ? order.event_name : ''}</Typography>
          <Box width={40} />
          <Typography variant="h6">{order.participant_name ? order.participant_name : ''}</Typography>
          <Box width={40} />
          <Typography variant="h6">Data: {formatDate(order.created_at)}</Typography>
          <Box width={40} />
          <Box sx={{ flexGrow: 1 }} />
          <FormControl size="small">
            <InputLabel id="state-label">Status</InputLabel>
            <Select
              labelId="state-label"
              id="state"
              value={state}
              label="state"
              onChange={handleChangeState}
            >
              <MenuItem value="RES">Zgłoszony</MenuItem>
              <MenuItem value="ACC">Zaakceptowany</MenuItem>
              <MenuItem value="ACT">Aktywny</MenuItem>
              <MenuItem value="GIN">Rezygnacja</MenuItem>
            </Select>
          </FormControl>
          <Box width={20} />
          <IconButton onClick={fullScreen ? handleExitFullScreen : handleEnterFullScreen}>
            <Iconify icon={fullScreen ? 'eva:collapse-fill' : 'eva:expand-fill'} width={20} height={20} />
          </IconButton>
          <IconButton onClick={handleConfirmClose}>
            <Iconify icon={'eva:close-fill'} width={20} height={20} />
          </IconButton>
        </Box>
        { fullScreen
          ? <FullScreenView
            updateOrders={updateOrders}
            refFormik={refFormik}
            handleConfirmClose={handleConfirmClose}
          />
          : <SimpleView positions={order.order_payments} order={order} />
        }
      </RootStyle>
      <SnackbarAlert open={openSnackbar.success} severity="success" message={message} onClose={handleCloseSnackbar} />
      <SnackbarAlert open={openSnackbar.error} severity="error" message={message} onClose={handleCloseSnackbar} />
      <ConfirmChangeStateModal open={openStateModal} handleClose={handleCloseStateModal} state={state} handleSubmit={handleStateSubmit} />
      <ConfirmCloseOrder open={openConfirmCloseOrderModal} handleClose={handleCloseConfirmCloseOrderModal} handleCloseOrder={handleClose} />
    </Portal>
  );
}
